import React, { useState, useCallback } from "react";
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import SearchBar from "./SearchBar";
import FilterSidebar from "./FilterSidebar";

/**
 * SearchAndFilterBar Component
 *
 * Combines search and filter functionality into a unified interface.
 * Shows active filter chips and provides easy access to filter sidebar.
 */
export default function SearchAndFilterBar({
  onSearchChange,
  onFiltersChange,
  searchValue = "",
  filters = {},
  filterOptions = {},
  placeholder = "Search...",
  showFilterButton = true,
  showActiveFilters = true,
  className = "",
  ...props
}) {
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  // Handle search change
  const handleSearchChange = useCallback(
    (searchTerm) => {
      if (onSearchChange) {
        onSearchChange(searchTerm);
      }
    },
    [onSearchChange]
  );

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (newFilters) => {
      if (onFiltersChange) {
        onFiltersChange(newFilters);
      }
    },
    [onFiltersChange]
  );

  // Remove specific filter
  const removeFilter = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.filter((v) => v !== value);

    const updatedFilters = {
      ...filters,
      [filterType]: newValues.length > 0 ? newValues : undefined,
    };

    // Remove undefined values
    Object.keys(updatedFilters).forEach((key) => {
      if (updatedFilters[key] === undefined) {
        delete updatedFilters[key];
      }
    });

    handleFiltersChange(updatedFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    handleFiltersChange({});
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).reduce(
    (count, filterArray) => count + (filterArray?.length || 0),
    0
  );

  // Get filter label for display
  const getFilterLabel = (filterType, value) => {
    const options = filterOptions[filterType] || [];
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  return (
    <>
      <div className={className}>
        {/* Top row: Search and Filter Button */}
        <div className="flex items-center justify-between space-x-4">
          <div className="flex-1">
            <SearchBar
              onSearch={handleSearchChange}
              placeholder={placeholder}
              initialValue={searchValue}
              {...props}
            />
          </div>

          {showFilterButton && (
            <button
              onClick={() => setIsFilterSidebarOpen(true)}
              className={`
              flex items-center space-x-2 px-4 py-2 border border-gray-300
              rounded-lg text-sm font-medium transition-colors duration-200
              ${
                activeFilterCount > 0
                  ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500
            `}
            >
              <FunnelIcon className="h-4 w-4" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
      {/* Filter Sidebar */}
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
        onFiltersChange={handleFiltersChange}
        filters={filters}
        filterOptions={filterOptions}
        title="Filter Results"
      />
    </>
  );
}
